import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, MessageCircle } from 'lucide-react';
import PageGlow from "@/components/PageGlow";
import HeroWave from "@/components/HeroWave";
import Eyebrow from "@/components/Eyebrow";
import { brand } from "@/config/brand";

const GRAD = '#5048E5';
const fg       = 'hsl(var(--foreground))';
const fgMuted  = 'hsl(var(--muted-foreground))';
const surface  = 'hsl(var(--card))';
const border   = 'hsl(var(--border))';
const orange   = 'hsl(243 75% 59%)';
import { FAQ_CATEGORIES } from './faq.seo';

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl transition-all" style={{ background: surface, border: `1px solid ${border}` }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
        aria-expanded={open}
      >
        <span className="pr-4 text-sm font-semibold leading-snug" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
          <ChevronDown size={16} style={{ color: fgMuted }} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <p className="px-6 pb-5 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// JSON-LD lives in ./faq.seo so route head() stays lightweight.

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <>


      {/* Hero */}
      <section className="relative overflow-hidden px-6 py-20" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <PageGlow tall />
        <div className="relative mx-auto max-w-3xl text-center">
          <div>
            <Eyebrow className="mb-4">FAQ</Eyebrow>
            <h1 className="mt-3 font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: fg, lineHeight: 1.1 }}>
              Questions answered.<br />In plain English.
            </h1>
            <p className="mt-4 text-base leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted, maxWidth: '48ch', margin: '1rem auto 0' }}>
              Everything you wanted to know about working with us — pricing, timelines, ownership, and more. Can't find your answer? Just ask.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Body */}
      <section className="px-6 pb-24" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-3xl">

          {/* Category tabs */}
          <div className="mb-8 flex flex-wrap gap-2">
            {FAQ_CATEGORIES.map(({ label }, i) => (
              <button
                key={label}
                onClick={() => setActiveCategory(i)}
                className="rounded-full px-4 py-1.5 text-xs font-semibold transition-all"
                style={{
                  background: activeCategory === i ? GRAD : 'hsla(243,75%,59%,0.08)',
                  color: activeCategory === i ? '#ffffff' : fgMuted,
                  border: `1px solid ${activeCategory === i ? GRAD : border}`,
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                }}>
                {label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-3"
            >
              {FAQ_CATEGORIES[activeCategory].faqs.map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="relative overflow-hidden px-6 py-16" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <div className="relative z-10 mx-auto max-w-2xl text-center">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: 'hsla(243,75%,59%,0.12)' }}>
            <MessageCircle size={22} style={{ color: orange }} />
          </div>
          <h2 className="mb-3 font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: fg }}>
            Still have questions?
          </h2>
          <p className="mb-6 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
            Get in touch and we'll answer everything — no pressure, no pitch, just an honest conversation.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/get-started"
              className="btn-glow group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all"
              style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Book a free call
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/services"
              className="btn-glass-hover inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all"
              style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(16px) saturate(140%)', WebkitBackdropFilter: 'blur(16px) saturate(140%)', border: '1px solid var(--glass-border)', boxShadow: 'var(--glass-shadow)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Explore services
            </Link>
            <a
              href={`mailto:${brand.contact.email}`}
              className="btn-glass-hover inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all"
              style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(16px) saturate(140%)', WebkitBackdropFilter: 'blur(16px) saturate(140%)', border: '1px solid var(--glass-border)', boxShadow: 'var(--glass-shadow)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Email us instead
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
